import { useRef } from "react";
import PhoneMockups from "@/assets/phone-mockups-2.png";
import { motion, useScroll, useTransform } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { handlePricingClick } from "@/components/HandlePricingClick";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toast";

/**
 * ContactSection component that displays a call-to-action with contact information
 * Features a phone mockup visual and gradient effects matching the site theme
 */
function ContactSection() {
  const { toast } = useToast();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const yParallax = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const basicTier = {
    name: "Basic",
    price: "9.99",
    period: "month",
    features: [
      "Basic access to LEXI's tips",
      "Daily racing insights",
      "Form guide analysis",
    ],
  };

  const handleClick = (tier) => {
    const result = handlePricingClick(tier);
    if (result.showToast) {
      toast({
        description: result.message,
        className: "bg-brand-accent text-black font-bold",
        duration: 3000,
      });
    }
  };

  return (
    <>
      <section
        id="contact"
        ref={sectionRef}
        className="relative pt-24 bg-black text-white overflow-hidden"
      >
        {/* Content */}
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Column - Text Content */}
            <div className="space-y-8 mb-10">
              <h2 className="text-4xl font-bold text-center md:text-left">
                Get in touch with the LEXI team
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed text-center md:text-left">
                We'd love to hear from you! Whether you have a question, a
                suggestion, or just want to say hello, our team is here
                to&nbsp;help.
              </p>
              <div className="space-y-4">
                <Button
                  onClick={() =>
                    (window.location.href = "mailto:hello@lexi.tips")
                  }
                  size="lg"
                  className="w-full rounded-lg px-8 py-4 md:w-auto bg-brand-accent hover:bg-brand-accent/80 text-black"
                >
                  Contact Us
                </Button>
              </div>
            </div>

            {/* Right Column - Phone Mockup */}
            <div className="relative h-full flex items-end justify-end">
              <motion.div
                initial={{ opacity: 0, scale: 0.65, y: 100 }}
                animate={{ opacity: 1, scale: 1, y: 10 }}
                transition={{ duration: 0.7, delay: 1.8 }}
                style={{ y: yParallax }}
                className="relative  w-full max-w-[900px] h-auto m-0 z-20 mt-0"
              >
                <img
                  src={PhoneMockups}
                  alt="Lexi on two phones: the public Race Results page and My Tips with saved races"
                  className="w-full max-w-[500px] mx-auto relative"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <Toaster />
    </>
  );
}

export default ContactSection;
